import React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import { BaseMultiSelect } from '../inputs';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

const defaultData = [];

/**
 * A multi select form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseMultiSelect>} BaseMultiSelectProps
 *
 * @param {BaseFieldRenderProps & BaseMultiSelectProps} fieldRenderProps
 * @returns {React.ReactElement} Multi select form field.
 */
const FormMultiSelect = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    value,
    onChange,
    dataItemKey = 'value',
    primitive,
    keysOnly,
    optional,
    readOnly,
    wrapperClassName,
    data = defaultData,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = React.useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';
  const mappedValues = value ? value
    ?.map((x) => (typeof x === 'object' || primitive ? x : data.find((item) => item[dataItemKey] == x))) : [];

  return (
    <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={!value || value.length === 0 ? null : value}
        {...(readOnly ? { className: 'label-readonly' } : {})}
      >
        <BaseMultiSelect
          value={(keysOnly) ? value : mappedValues.filter((x) => x !== undefined)}
          onChange={onChange}
          keysOnly={keysOnly}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          name={name}
          readOnly={readOnly}
          optional={optional}
          disabled={disabled}
          dataItemKey={dataItemKey}
          primitive={primitive}
          data={data}
          dataTestId={editorId}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormMultiSelect;
